import { combineReducers, AnyAction } from "redux";
import { PagedResponseEmpty } from "@ofqual/portal-core/core/types";
import {
  LOADING_ORGANISATION_LIST,
  LOADED_ORGANISATION_LIST,
  LOADING_ORGANISATION,
  LOADED_ORGANISATION,
  EDIT_ORGANISATION,
  EDIT_ORGANISATION_SUCCESS,
  EDIT_ORGANISATION_ENGAGEMENT,
  EDIT_ORGANISATION_ENGAGEMENT_SUCCESS,
  LOADING_APPLICATION,
  LOADED_APPLICATION,
  EDIT_APPLICATION,
  EDIT_APPLICATION_SUCCESS,
  APPROVE_APPLICATION,
  REJECT_APPLICATION,
  WITHDRAW_APPLICATION,
  LOADING_EXPANSION_APPLICATION_LIST,
  LOADED_EXPANSION_APPLICATION_LIST,
  LOADING_ORGANISATION_SCOPES,
  LOADED_ORGANISATION_SCOPES,
  LOADING_APPLICATION_SCOPES,
  LOADED_APPLICATION_SCOPES,
  TOGGLE_DELETE_SCOPE,
  DELETE_SCOPES,
  UPDATE_SCOPE,
  ADD_SCOPE,
  UPDATE_ORGS_PARAMS,
  LOADED_SHARE,
} from "./constants";
import {
  ExpansionApplicationListState,
  OrganisationListState,
  OrganisationScopesState,
  OrganisationState,
  RecognitionApplicationState,
  RecognitionApplicationScopesState,
} from "./state";
import { noCrudResult, successCrudResult } from "./types";
import { Scope } from "@ofqual/portal-organisations-scope/dist/module/domain/Scope";
import { LookupTrackerStore } from "@ofqual/portal-core/core/types";
import deepmerge from "deepmerge";

export const organisationListInitialState: OrganisationListState = {
  status: "NOT_LOADED",
  response: PagedResponseEmpty,
  hasData: false,
};

export const organisationInitialState: OrganisationState = {
  status: "NOT_LOADED",
  org: null,
  share: null,
  lastCrudResult: noCrudResult(),
};

export const applicationInitialState: RecognitionApplicationState = {
  status: "NOT_LOADED",
  application: null,
  lastCrudResult: noCrudResult(),
};

const expansionApplicationListInitialState: ExpansionApplicationListState = {
  status: "NOT_LOADED",
  response: PagedResponseEmpty,
  hasData: false,
  itemId: null,
};

export const organisationScopesInitialState: OrganisationScopesState = {
  status: "NOT_LOADED",
  searchParams: {},
  scopes: null,
};

export const recognitionApplicationScopesInitialState: RecognitionApplicationScopesState = {
  status: "NOT_LOADED",
  lookupTrackerStore: {},
  scopes: [],
  lastCrudResult: noCrudResult(),
};

export const defaultActivityFeedStateDictionary = {};

export const organisationList = (
  state: OrganisationListState = organisationListInitialState,
  action: AnyAction
): OrganisationListState => {
  const { type, payload } = action;
  switch (type) {
    case LOADING_ORGANISATION_LIST:
      return {
        ...state,
        status: "LOADING",
      };
    case LOADED_ORGANISATION_LIST:
      return {
        ...state,
        status: "LOADED",
        response: payload,
        hasData: true,
      };
  }
  return state;
};

export const organisation = (
  state: OrganisationState = organisationInitialState,
  action: AnyAction
): OrganisationState => {
  const { type, payload } = action;

  switch (type) {
    case LOADING_ORGANISATION:
      return {
        ...state,
        status: "LOADING",
        lastCrudResult: noCrudResult(),
        org: null,
      };
    case EDIT_ORGANISATION:
    case EDIT_ORGANISATION_ENGAGEMENT:
      return {
        ...state,
        status: "UPDATING",
        lastCrudResult: noCrudResult(),
      };

    case LOADED_ORGANISATION:
      return {
        ...state,
        status: "LOADED",
        org: payload.organisation,
        share: payload.share ?? null,
        lastCrudResult: noCrudResult(),
      };
    case EDIT_ORGANISATION_SUCCESS:
    case EDIT_ORGANISATION_ENGAGEMENT_SUCCESS:
      return {
        ...state,
        status: "LOADED",
        org: payload,
        lastCrudResult: successCrudResult(),
      };
    case LOADED_SHARE:
      return {
        ...state,
        share: payload,
      };
  }
  return state;
};

export const application = (
  state: RecognitionApplicationState = applicationInitialState,
  action: AnyAction
): RecognitionApplicationState => {
  const { type, payload } = action;
  switch (type) {
    case LOADING_APPLICATION:
      return {
        ...state,
        status: "LOADING",
        lastCrudResult: noCrudResult(),
        application: null,
      };
    case EDIT_APPLICATION:
    case APPROVE_APPLICATION:
    case REJECT_APPLICATION:
    case WITHDRAW_APPLICATION:
      return {
        ...state,
        status: "UPDATING",
        lastCrudResult: noCrudResult(),
      };

    case LOADED_APPLICATION:
      return {
        ...state,
        status: "LOADED",
        application: payload,
        lastCrudResult: noCrudResult(),
      };
    case EDIT_APPLICATION_SUCCESS:
      return {
        ...state,
        status: "LOADED",
        application: payload,
        lastCrudResult: successCrudResult(),
      };
  }
  return state;
};

export const expansionApplicationList = (
  state: ExpansionApplicationListState = expansionApplicationListInitialState,
  action: AnyAction
): ExpansionApplicationListState => {
  const { type, payload } = action;
  switch (type) {
    case LOADING_EXPANSION_APPLICATION_LIST:
      return {
        ...state,
        status: "LOADING",
      };
    case LOADED_EXPANSION_APPLICATION_LIST:
      return {
        ...state,
        status: "LOADED",
        response: payload.pagedResponse,
        hasData: true,
        itemId: payload.itemId,
      };
  }
  return state;
};

export const organisationScopes = (
  state: OrganisationScopesState = organisationScopesInitialState,
  action: AnyAction
): OrganisationScopesState => {
  const { type, payload } = action;
  switch (type) {
    case LOADING_ORGANISATION_SCOPES:
      return {
        ...state,
        status: "LOADING",
        scopes: null,
      };

    case LOADED_ORGANISATION_SCOPES:
      return {
        ...state,
        status: "LOADED",
        scopes: payload,
      };
    case UPDATE_ORGS_PARAMS:
      return {
        ...state,
        searchParams: { ...payload },
      };
  }
  return state;
};

export const applicationScopes = (
  state: RecognitionApplicationScopesState = recognitionApplicationScopesInitialState,
  action: AnyAction
): RecognitionApplicationScopesState => {
  const { type, payload } = action;
  switch (type) {
    case ADD_SCOPE:
      return {
        ...state,
        scopes: [...state.scopes, payload],
      };
    case LOADING_APPLICATION_SCOPES:
      return {
        ...state,
        status: "LOADING",
        scopes: [],
      };

    case LOADED_APPLICATION_SCOPES:
      return {
        ...state,
        status: "LOADED",
        scopes: payload,
      };
    case TOGGLE_DELETE_SCOPE:
      const { scopes } = payload as {
        scopes: Scope[];
        shouldDelete: boolean;
      };

      const newScopes = [...state.scopes];
      for (const scope of scopes) {
        const foundIndex = newScopes.findIndex((x) => x.id === scope.id);
        if (foundIndex !== -1) {
          newScopes[foundIndex] = {
            ...newScopes[foundIndex],
            shouldDelete: !scope.shouldDelete,
          };
        }
      }
      return {
        ...state,
        scopes: newScopes,
      };
    case DELETE_SCOPES:
      const items = state.scopes.filter((x) => x.shouldDelete !== true);
      return {
        ...state,
        scopes: items,
      };
    case UPDATE_SCOPE:
      const { updatedScope, lookupTrackingInfo } = payload as {
        updatedScope: Scope;
        lookupTrackingInfo: LookupTrackerStore;
      };
      const updatedScopes = state.scopes.map((item) =>
        item.id === updatedScope.id ? updatedScope : item
      );
      return {
        ...state,
        scopes: updatedScopes,
        lookupTrackerStore: deepmerge(
          state.lookupTrackerStore,
          lookupTrackingInfo
        ),
      };
  }
  return state;
};

export const rootReducer = combineReducers({
  organisationList,
  organisation,
  application,
  expansionApplicationList,
  organisationScopes,
  applicationScopes,
});
